import intersection from 'lodash/intersection';
import {navigate, useLocation} from '@reach/router';
import {useStaticQuery, graphql} from 'gatsby';
import styled from 'styled-components';
import React, {FC} from 'react';

import {fontSizesMixins, fontWeights, palette} from '../../lib/styleVariables';
import {FilterCategory, FilterCategoryProps} from './filterCells';
import {Handler} from '../../lib/typeHelpers';

export interface FilterCategoryItem {
  title: string;
  number?: number;
  key: string;
}

export interface FilterCategoryType {
  title: string;
  key: string;
  items: Array<FilterCategoryItem>;
}

interface FilterProps {
  categories: Array<FilterCategoryType>;
  onFilterChange?: (selectedKeys: string[]) => void;
  onlyOne?: boolean;
  onReset?: Handler;
  resetText?: string;
  titleText?: string;
  headerCss?: string;
  itemRenderer?: FilterCategoryProps['itemRenderer'];
  isExpanded?: boolean;
  updateUrlOnFilterChange?: boolean;
  selectedKeys?: string[];
}

const StyledTitleText = styled.p`
  ${fontSizesMixins.text3()};
  margin: 0 8px 8px 0;
  color: ${palette.black.base};
  font-weight: ${fontWeights.medium};
`;

const filterHeaderHiddenStyles = `
  opacity: 0;
  visibility: hidden;
`;

const StyledFilterHeader = styled.div<{$css?: string; isHidden?: boolean}>`
  height: 34px;
  display: flex;
  justify-content: space-between;
  padding: 0 4px;
  ${props => (props.isHidden ? filterHeaderHiddenStyles : '')}
  ${props => props.$css}
`;

const StyledContainer = styled.div`
  width: 100%;
`;

const StyledResetText = styled.p`
  ${fontSizesMixins.text3()};
  color: ${palette.black.base};
  font-weight: ${fontWeights.bold};
  cursor: pointer;
  margin: 0 0 8px auto;
  &:hover {
    color: ${palette.blue.base};
  }
`;

// seperator for multiple assignments on route
const SEPERATOR: string = '|';

// Removes given value from the array and returns a new array
const removeElementFromArray = (value: string, arr: any[]) => {
  const elementIndex = arr.indexOf(value);
  const clonedArray = [...arr];
  clonedArray.splice(elementIndex, 1);

  return clonedArray;
};

let siteUrl: string;

// @TODO find a better way to do this and make this DRY (filterR4.tsx)
export const getLocationUrl = (): string => {
  // Browser
  if (window?.location?.href)
    return window.location.href;

  // SSR
  const {pathname} = useLocation();

  if (!siteUrl) {
    const data = useStaticQuery(
      graphql`
        query {
          site {
            siteMetadata {
              siteUrl
            }
          }
        }
      `
    );

    siteUrl = data.site.siteMetadata.siteUrl;
  }

  return pathname ? siteUrl + pathname : '';
};

const getFiltersFromUrl = () => {
  const url = getLocationUrl();
  if (!url)
    return [];

  const params: string = new URL(url).searchParams.get('filter') || '';
  if (params)
    return params.split(SEPERATOR);

  return [];
};

export const updateUrl = (itemKeys: string[]) => {
  const url = getLocationUrl();
  if (url) {
    const urlObj = new URL(url);
    const urlWithoutSearchParams = new URL(urlObj.pathname, urlObj.origin);

    if (itemKeys.length > 0)
      urlWithoutSearchParams.searchParams.set('filter', itemKeys.join(SEPERATOR));
    navigate(urlWithoutSearchParams.toString(), {replace: true});
  }
};

export const Filter: FC<FilterProps> = props => {
  const selectedItemFromUrl = React.useMemo(getFiltersFromUrl, [getLocationUrl()]);
  const [selectedItemKeys, setSelectedItemKeys] = React.useState<string[]>(selectedItemFromUrl);
  const hasHeader = props.titleText || selectedItemKeys.length !== 0;
  const allKeys = React.useMemo(() => {
    const arr: string[] = [];
    props.categories.forEach(category => arr.push(...category.items.map(item => item.key)));

    return arr;
  }, [JSON.stringify(props.categories)]);

  const onResetClick = () => {
    updateSelectedItemKeys([]);

    if (props.onReset)
      props.onReset();
  };

  const onChange = (item: FilterCategoryItem, isChecked: boolean) => {
    let newSelectedItemKeys: Array<string> = [];
    const prevSelectedItemKeys = props.onlyOne ? [] : selectedItemKeys;
    if (isChecked)
      newSelectedItemKeys = [...prevSelectedItemKeys, item.key];
    else
      newSelectedItemKeys = removeElementFromArray(item.key, selectedItemKeys);

    updateSelectedItemKeys(newSelectedItemKeys);
  };

  const updateSelectedItemKeys = React.useCallback((itemKeys: string[]) => {
    setSelectedItemKeys(itemKeys);

    if (props.updateUrlOnFilterChange)
      updateUrl(itemKeys);
  }, []);

  React.useEffect(() => {
    const newVal = selectedItemKeys.length ? selectedItemKeys : [];
    if (props.onFilterChange)
      props.onFilterChange(newVal);
  }, [selectedItemKeys, allKeys]);

  return (
    <StyledContainer>
      <StyledFilterHeader $css={props.headerCss} isHidden={!hasHeader}>
        {props.titleText && <StyledTitleText>{props.titleText}</StyledTitleText>}
        {selectedItemKeys.length !== 0 && (
          <StyledResetText onClick={onResetClick}>{props.resetText}</StyledResetText>
        )}
      </StyledFilterHeader>
      <div>
        {props.categories.map((category: FilterCategoryType) => {
          const preSelectedKeys = props.selectedKeys || [];
          const categoryItems = category.items.map(item => item.key);
          const hasFilter =
            intersection(selectedItemKeys, categoryItems).length > 0 ||
            intersection(preSelectedKeys, categoryItems).length > 0;
          const [isExpanded, setIsExpanded] = React.useState(hasFilter || props.isExpanded);

          React.useEffect(() => {
            if (hasFilter)
              setIsExpanded(true);
          }, [props.selectedKeys]);

          return (
            <FilterCategory
              itemRenderer={props.itemRenderer}
              key={category.key}
              categoryKey={category.key}
              categoryTitle={category.title}
              items={category.items}
              isExpanded={isExpanded}
              onFilterChange={onChange}
              onToggle={setIsExpanded}
              selectedItemKeys={selectedItemKeys}
            />
          );
        })}
      </div>
    </StyledContainer>
  );
};
